import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Box, Typography } from '@mui/material';
import { Link, useLocation } from '@tanstack/react-router';
import React, { useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { getParticipationRoute } from '@/helpers/participationNavigation';
import StrategyIcon from '@/icons/LightBulb2Icon';
import NewsPaperClippingIcon from '@/icons/NewsPaperClippingIcon';
import TargetIcon from '@/icons/TargetIcon';
import { RailNavItem } from '@/layouts/layout.types';
import GuideToggleButton from '@/layouts/socialFeed/GuideToggleButton';
import SocialFeedRailNavItem from '@/layouts/socialFeed/SocialFeedRailNavItem';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';

const InsightsRailMenu = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const insightsMenuItems = [
    { label: 'Dashboard', path: '/report/our_journey' },
    { label: 'Planner', path: '/report/our_calendar' },
    { label: 'Timeline', path: '/report/our_songline' }
  ];

  const isInsightsPageActive = insightsMenuItems.some(item => pathname.startsWith(item.path));

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  return (
    <Box
      data-tour='insights-menu'
      sx={{ position: 'relative', width: '100%' }}
      onMouseEnter={() => {
        clearCloseTimer();
        setIsOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
          cursor: 'pointer',
          color: isInsightsPageActive ? '#CC4E00' : '#000000',
          width: '100%',
          '&:hover': {
            bgcolor: '#CC4E0022'
          }
        }}
      >
        <StrategyIcon className='h-[28px] w-[28px]' />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            textAlign: 'center',
            color: isInsightsPageActive ? '#CC4E00' : '#000000',
            fontFamily: 'Inter'
          }}
        >
          Insights
        </Typography>
      </Box>

      {isOpen ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: '110%',
            width: 220,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            border: '1px solid',
            borderColor: 'divider',
            zIndex: 1300
          }}
        >
          <ul className='p-2'>
            {insightsMenuItems.map(item => (
              <li key={item.path} onClick={scheduleClose}>
                <Link
                  to={item.path}
                  className='block select-none rounded-md p-3 text-sm font-medium leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                  activeProps={{
                    className: 'bg-accent text-accent-foreground'
                  }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </Box>
      ) : undefined}
    </Box>
  );
};

const SocialFeedRailNav = () => {
  const isSocialFeedEnabled = useGlobalSettingsStore(useShallow(state => state.globalSettings.isSocialFeedEnabled));
  const participationRoute = getParticipationRoute(isSocialFeedEnabled);
  const navTopItems: RailNavItem[] = [
    isSocialFeedEnabled
      ? { icon: NewsPaperClippingIcon, label: 'Updates', path: participationRoute, dataTour: 'updates-menu' }
      : { icon: GroupsOutlinedIcon, label: 'Collaborate', path: participationRoute, dataTour: 'collaborate-menu' },
    { icon: TargetIcon, label: 'Strategy', path: '/sstra', dataTour: 'strategy-menu' }
  ];

  return (
    <Box
      sx={{
        width: '64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        pt: 2,
        bgcolor: 'background.paper',
        fontFamily: 'Inter'
      }}
    >
      <div className='flex w-full flex-1 flex-col gap-2'>
        {navTopItems.map(item => (
          <SocialFeedRailNavItem key={item.path} item={item} />
        ))}
        <InsightsRailMenu />
      </div>
      <GuideToggleButton />
    </Box>
  );
};

export default SocialFeedRailNav;
